/**
 * Runtime-agnostic analytics core.
 *
 * Nothing here touches `window`, `process` or `fetch`. Each runtime supplies a
 * `AnalyticsSink` (see ./browser, ./node, ./workers) and this module composes
 * them into one client with a single event contract, so a call site reads the
 * same everywhere.
 */

import { normalizeEnvironment, sendsEvents } from './environment'
import { identityLinkProperties, noHandoffStore } from './identity'
import type {
  AnalyticsClient,
  AnalyticsClientOptions,
  AnalyticsEvent,
  AnalyticsIdentity,
  AnalyticsProperties,
  AnalyticsSink,
} from './types'

export type {
  AnalyticsClient,
  AnalyticsClientOptions,
  AnalyticsEvent,
  AnalyticsIdentity,
  AnalyticsProperties,
  AnalyticsSink,
  AnalyticsSurface,
} from './types'

/**
 * A client that drops everything. Returned instead of `null` wherever config is
 * missing, so no call site needs to null-check before tracking.
 */
export const noopAnalytics: AnalyticsClient = {
  surface: 'script',
  capture: () => {},
  captureException: () => {},
  identify: () => {},
  alias: () => {},
  signIn: () => {},
  signOut: () => {},
  getDistinctId: () => undefined,
  flush: async () => {},
  shutdown: async () => {},
}
export type { ClientFlags, FlagContext, FlagReader, FlagReadOptions, FlagValue } from './flags'
export { POSTHOG_ASSET_HOST, POSTHOG_HOST, POSTHOG_PROJECT_ID, POSTHOG_PROJECT_KEY } from './config'
import { ANALYTICS_EVENTS } from './events'
export { ANALYTICS_EVENTS } from './events'
export { FEATURE_FLAGS, type FeatureFlagKey } from './flag-keys'
export { mirrorSentryEvent, sentryExceptionProperties, type SentryEventLike } from './sentry'
export { normalizePath, viewNameFromPath } from './path'
export { legacyEventName, legacyEventProperties, UNKNOWN_LEGACY_EVENT } from './legacy'
export { localSink, normalizeEnvironment, sendsEvents, shouldSend } from './environment'
export { identityLinkProperties, memoryHandoffStore, noHandoffStore } from './identity'
export type { IdentityHandoffStore, IdentityLinkProperties } from './identity'
export type { AnalyticsEnvironment, EnvironmentOptions } from './environment'
export type {
  AnalyticsEventName,
  ElementKind,
  InteractionProperties,
  InteractionTrigger,
  RequestProperties,
  ViewKind,
  ViewShownProperties,
} from './events'

export function createAnalytics(options: AnalyticsClientOptions): AnalyticsClient {
  const { sinks, surface, getIdentity, baseProperties, onError, environment, forceSend, localSink, onEvent } = options
  const handoff = options.handoffStore ?? noHandoffStore()

  const deployment = normalizeEnvironment(environment)
  const sends = sendsEvents({ environment, forceSend, localSink })

  const resolveBaseProperties = (): AnalyticsProperties => {
    if (typeof baseProperties === 'function') {
      return baseProperties()
    }
    return baseProperties ?? {}
  }

  /**
   * Dispatches to every sink and reports failures rather than propagating them.
   *
   * One shape for both the fire-and-forget calls and the awaited ones, so a
   * sink that throws synchronously and one that rejects are handled the same.
   * The rejection handler is attached unconditionally: an earlier version
   * attached it only when swallowing, so the non-swallowing mode turned every
   * async sink failure into an unhandled rejection, which on Node >= 15 takes
   * the process down.
   * A sink failing must never take down the call site: an analytics blip is
   * not worth a broken click handler.
   */
  const fanOut = (operation: string, work: (sink: AnalyticsSink) => unknown): Array<Promise<void>> =>
    sinks.map((sink) => {
      // Dispatched synchronously on purpose. Deferring even by a microtask
      // would let a `pagehide` fire before posthog-js had buffered the event.
      try {
        return Promise.resolve(work(sink)).then(
          () => undefined,
          (error: unknown) => onError?.(error, { sink: sink.name, operation }),
        )
      } catch (error) {
        onError?.(error, { sink: sink.name, operation })
        return Promise.resolve()
      }
    })

  const resolveIdentity = (override?: AnalyticsIdentity): AnalyticsIdentity | undefined => {
    return override ?? getIdentity?.()
  }

  // Decided once, not per call: whether this deployment sends is fixed for the
  // client's life. Testing it inside every method meant four branches that
  // could only ever agree, and one of them being forgotten is how a dev build
  // starts writing to the production project.
  if (!sends) {
    return {
      ...noopAnalytics,
      surface,
      capture(name, properties, identityOverride) {
        // Reported rather than dropped silently, so a developer sees the event
        // they just triggered without it reaching the project real funnels are
        // read from. Mirrors what the Amplitude adapters do in dev.
        const identity = identityOverride ?? getIdentity?.()
        if (identity?.distinctId) {
          onEvent?.({ name, properties: { ...properties, surface, environment: deployment } }, identity)
        }
      },
    }
  }

  const client: AnalyticsClient = {
    surface,

    capture(name, properties, identityOverride) {
      let identity: AnalyticsIdentity | undefined
      let base: AnalyticsProperties
      try {
        // Both are caller-supplied thunks evaluated at capture time, outside
        // any sink, so nothing else catches them. The renderer's reads
        // `process.platform` and `window.__RENDERER_NAME`.
        identity = resolveIdentity(identityOverride)
        base = resolveBaseProperties()
      } catch (error) {
        onError?.(error, { sink: 'core', operation: 'capture' })
        return
      }

      if (!identity?.distinctId) {
        // Without a distinct id PostHog would invent one per request and
        // shatter the person. Dropping is the lesser evil.
        onError?.(new Error(`Dropped "${name}": no distinct id`), { sink: 'core', operation: 'capture' })
        return
      }

      // `surface` and `environment` last: a stray call-site property must not
      // be able to mislabel which runtime or deployment emitted the event.
      // `environment` is what keeps staging and beta traffic filterable inside
      // the one project every environment shares.
      const event: AnalyticsEvent = {
        name,
        properties: { ...base, ...properties, surface, environment: deployment },
      }

      void fanOut('capture', (sink) => sink.capture(event, identity))
    },

    captureException(error, properties, identityOverride) {
      let identity: AnalyticsIdentity | undefined
      let base: AnalyticsProperties
      try {
        identity = resolveIdentity(identityOverride)
        base = resolveBaseProperties()
      } catch (thrown) {
        onError?.(thrown, { sink: 'core', operation: 'captureException' })
        return
      }

      if (!identity?.distinctId) {
        onError?.(new Error('Dropped exception: no distinct id'), { sink: 'core', operation: 'captureException' })
        return
      }

      const merged: AnalyticsProperties = { ...base, ...properties, surface, environment: deployment }
      void fanOut('captureException', (sink) => sink.captureException?.(error, merged, identity))
    },

    identify(identity, properties) {
      if (!identity.distinctId) {
        return
      }
      void fanOut('identify', (sink) => sink.identify?.(identity, properties))
    },

    alias(identity, previousId) {
      if (!identity.distinctId || !previousId || identity.distinctId === previousId) {
        return
      }

      // An alias is irreversible in PostHog. Folding an anonymous device id
      // into the account it became is the intended use; folding one account
      // into another destroys the separation between, say, a work and a
      // personal login, and nothing later can undo it. The previous account is
      // the one a caller is most likely to reach for, and the only one the core
      // can recognise, so it is the one refused. Multi-account linking goes
      // through `previous_logged_in_user_id` instead.
      const previousAccount = identity.previousUserId ?? handoff.read()
      if (previousAccount && previousId === previousAccount) {
        onError?.(new Error(`Refused to alias account "${previousId}" onto another account`), {
          sink: 'core',
          operation: 'alias',
        })
        return
      }

      void fanOut('alias', (sink) => sink.alias?.(identity, previousId))
    },

    signIn(identity, properties) {
      const previousUserId = identity.previousUserId ?? handoff.read()
      const resolved: AnalyticsIdentity = { ...identity, previousUserId }
      const link = identityLinkProperties(resolved)

      // Anonymous history first, so it lands on the account rather than being
      // stranded on a device id nothing points at any more.
      if (resolved.anonymousId && resolved.anonymousId !== resolved.distinctId) {
        client.alias(resolved, resolved.anonymousId)
      }

      client.identify(resolved, { ...link, ...properties })
      client.capture(ANALYTICS_EVENTS.USER_SIGNED_IN, { ...link, ...properties }, resolved)

      // Consumed: the next sign-in should link to this account, not to one two
      // sign-ins ago.
      handoff.write(undefined)
    },

    signOut(identityOverride) {
      const identity = identityOverride ?? getIdentity?.()

      // Captured before the reset so it attributes to the account leaving,
      // which is the only moment that attribution is still correct.
      if (identity?.distinctId) {
        client.capture(ANALYTICS_EVENTS.USER_SIGNED_OUT, undefined, identity)
      }

      // Held across the reset so the next sign-in can say what this device was
      // signed into before, without the two accounts ever being merged.
      if (identity?.userId) {
        handoff.write(identity.userId)
      }

      void fanOut('reset', (sink) => sink.reset?.())
    },

    // Every adapter already resolves identity for capture, and the browser's
    // resolver reads posthog-js's own distinct id, so there is nothing
    // runtime-specific to implement here.
    getDistinctId() {
      try {
        return resolveIdentity()?.distinctId
      } catch {
        return undefined
      }
    },

    async flush() {
      await Promise.all(fanOut('flush', (sink) => sink.flush?.()))
    },

    async shutdown() {
      await Promise.all(fanOut('shutdown', (sink) => sink.shutdown?.()))
    },
  }

  return client
}
